/**
 * @fileoverview Workspace Agent — universal AI chat across every accessible job
 *
 * URL: /dashboard/agent
 *
 * Reads the active org from `useActiveOrg()` and renders the workspace
 * agent surface. Available to any authenticated member of the org;
 * access to the underlying data is enforced by the Convex backend.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import { Loader2 } from "lucide-react";
import { useActiveOrg } from "@/utils/org-context";
import { WorkspaceAgentView } from "@/components/agents/universal";

interface AgentRouteSearch {
  /** Seed query forwarded by the Cmd+K command palette. */
  ask?: string;
}

export const Route = createFileRoute("/_app/_auth/dashboard/_layout/agent")({
  component: WorkspaceAgentPage,
  validateSearch: (search: Record<string, unknown>): AgentRouteSearch => ({
    ask: typeof search.ask === "string" ? search.ask : undefined,
  }),
});

function WorkspaceAgentPage() {
  const { data: user, isLoading } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const { activeOrg, isLoading: orgLoading } = useActiveOrg();
  const { ask } = Route.useSearch();

  if (isLoading || orgLoading || !user) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!activeOrg) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-sm text-muted-foreground">
          Select an organisation to use the agent.
        </p>
      </div>
    );
  }

  return <WorkspaceAgentView orgId={activeOrg._id} initialQuery={ask} />;
}
